import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import {
  VueAccountManagementService,
  VueFormError,
  VueSecurityQuestionFormOutput,
  VueSecurityQuestionGroup,
  VueSecurityQuestionsComponent,
  VueServerMessage,
  VueServerMessageType,
  VueSessionCookieService,
  VueTranslationService
} from '@vue/vueframework';
import { getFirstErrorControl } from '../shared/connect.util';
import { AccountMaintenanceMessagesService } from '../shared/services/account-maintenance-messages.service';

@Component({
  selector: 'app-security-questions',
  templateUrl: 'security-questions.component.html',
  standalone: false
})
export class SecurityQuestionsComponent implements OnInit {
  @ViewChild('myForm', { static: false }) public myForm: NgForm;

  @ViewChild('securityQuestionsComponent', { static: false })
  public securityQuestionsComponent: VueSecurityQuestionsComponent;

  /**
   * The password entered in the current password field
   */
  public currentPassword = '';

  /**
   * List of security questions used for the account management page
   */
  public securityQuestions: VueSecurityQuestionGroup[] = [];

  /**
   * Questions selected by the user
   */
  public selectedQuestions: number[] = [];

  /**
   * selected questions by the user in edit mode
   */
  public selectedQuestionsSaved: number[] = [];

  /**
   * Security questions page view mode 'edit' | 'view'
   */
  public viewMode: 'view' | 'edit' | 'answer';

  /**
   * Is the security questions page in edit mode
   */
  public get editMode(): boolean {
    return this.viewMode === 'edit';
  }

  /**
   * form error Object containing the details of an invalid form
   */
  public formError = new VueFormError();

  /**
   * user input from the security questions edit form
   */
  public formInput: VueSecurityQuestionFormOutput;

  public constructor(
    private route: ActivatedRoute,
    private vueAccountManagementService: VueAccountManagementService,
    private vueTranslationService: VueTranslationService,
    private vueSessionCookieService: VueSessionCookieService,
    private accountMaintenanceMessagesService: AccountMaintenanceMessagesService
  ) {}

  public ngOnInit(): void {
    this.viewMode = 'view';
    this.securityQuestions = this.route.snapshot.data.securityQuestions;
    this.selectedQuestions = this.route.snapshot.data.selectedQuestions;
  }

  /**
   * Method to set the page in edit mode
   */
  public editSecurityQuestions(): void {
    this.viewMode = 'edit';
    this.selectedQuestionsSaved = [ ...this.selectedQuestions ];
    this.selectedQuestions = [];
  }

  /**
   * Method to update the form input from user
   * @param event has the form details
   */
  public updateFormInput(event: VueSecurityQuestionFormOutput): void {
    this.formInput = event;
  }

  /**
   * Method to set the security question page to view mode
   */
  public changeToViewMode(): void {
    this.viewMode = 'view';
    this.selectedQuestions = [ ...this.selectedQuestionsSaved ];
    this.selectedQuestionsSaved = [];
  }

  /**
   * Save the new security questions and answers
   */
  public save(): void {
    if (this.myForm.form.valid && this.formInput.valid) {
      const userId = this.vueSessionCookieService.getAttribute('appUserId');
      this.vueAccountManagementService.setUserSecurityQuestions(Number(userId),
        this.formInput.answers,
        this.currentPassword)
        .subscribe(
          {
            error: (error) => {
              this.handleServerError(error);
            },
            next: () => {
              this.viewMode = 'view';
              this.selectedQuestionsSaved = [];
              this.selectedQuestions = this.formInput.answers.map((a) => a.questionId);
              this.accountMaintenanceMessagesService.setMessage(
                new VueServerMessage(
                  VueServerMessageType.Success,
                  this.vueTranslationService
                    .getTranslationFromBundle('PortalBase', 'accountMaintenance.successMessageSecurityQuestionsPage'),
                  true)
              );
            }
          }
        );
    } else if (this.myForm.form.valid === false) {
      const errorControl = getFirstErrorControl(this.myForm);
      this.formError.invalidFormSubmit = true;
      this.formError.fieldInError = errorControl;
    } else {
      const errorControl = getFirstErrorControl(this.securityQuestionsComponent.securityQuestionsForm);
      this.formError.invalidFormSubmit = true;
      this.formError.fieldInError = errorControl;
    }
  }

  /**
   * This method is used to handle any errors during the service calls
   *
   * @param response Error response object
   */
  public handleServerError(response: HttpErrorResponse): void {
    const errorCode = response.error[ 0 ];
    this.accountMaintenanceMessagesService.setMessage(
      new VueServerMessage(
        VueServerMessageType.Error,
        this.vueTranslationService.getTranslationFromServerCode(errorCode),
        true)
    );
  }
}
